import React, { useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '@/App';
import { playNotificationSound, showBrowserNotification } from './NotificationManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Global message notifier for customers — mirrors TradieChatPopup toast behaviour.
 * Polls conversations every 3s and alerts when new unread messages arrive.
 */
export default function CustomerChatNotifier() {
  const { user } = useContext(AuthContext);
  const prevUnreadRef = useRef(0);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!user || user.role === 'tradie') return;

    const checkConversations = async () => {
      try {
        const response = await axios.get(`${API}/chat/conversations`, {
          withCredentials: true
        });
        const totalUnread = response.data.reduce((sum, conv) => sum + conv.unread_count, 0);

        if (!initializedRef.current) {
          initializedRef.current = true;
          prevUnreadRef.current = totalUnread;
          return;
        }

        if (totalUnread > prevUnreadRef.current) {
          playNotificationSound(0.6);

          const latestConv = response.data.find(c => c.unread_count > 0);
          const senderName = latestConv?.other_user?.name || 'A tradie';

          toast.success('New message from a tradie!', {
            description: `${senderName} sent you a message`,
            duration: 8000,
            action: {
              label: 'View',
              onClick: () => { window.location.href = '/account?tab=chat'; }
            }
          });

          if (document.hidden) {
            showBrowserNotification(
              `New message from ${senderName}`,
              ((latestConv?.last_message?.message || '').replace(/\s+/g, ' ').trim().slice(0, 120)) || 'You have a new message on MyLocalTradie',
              () => {
                window.focus();
                window.location.href = '/account?tab=chat';
              }
            );
          }
        }

        prevUnreadRef.current = totalUnread;
      } catch {
        // Ignore polling errors
      }
    };

    checkConversations();
    const interval = setInterval(checkConversations, 3000);
    return () => clearInterval(interval);
  }, [user]);

  return null;
}
